import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem,
    TablePagination
} from "@mui/material";

import { Visibility, Edit, Delete, Add } from "@mui/icons-material";

import {
    collection,
    getDocs,
    deleteDoc,
    doc,
    updateDoc,
    orderBy,
    query
} from "firebase/firestore";

import { db } from "../../firebase";
import Swal from "sweetalert2";

function Viewpurchasse() {

    const [purchases, setPurchases] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [dealers, setDealers] = useState([]);
    const [products, setProducts] = useState([]);

    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const rowsPerPage = 10;
    const [openItems, setOpenItems] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [purchaseId, setPurchaseId] = useState("");

    const [openEdit, setOpenEdit] = useState(false);
    const [editData, setEditData] = useState({
        purchaseno: "",
        dealerId: "",
        purchasedate: ""
    });

    useEffect(() => {
        loadPurchases();
        loadDealers();
        loadProducts();
    }, []);

    const loadPurchases = async () => {
    try {
        // Create a query reference
        const q = query(
            collection(db, "purchases"), 
            orderBy("purchasedate", "desc")
        );
        
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        setPurchases(data);
        setFiltered(data);
    } catch (error) {
        console.error("Error loading purchases:", error);
    }
};

    const loadDealers = async () => {
        const snap = await getDocs(collection(db, "dealers"));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setDealers(data);
    };

    const loadProducts = async () => {
        const snap = await getDocs(collection(db, "purchase_products"));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setProducts(data);
    };

    const getDealerName = (dealerId) => {
        const dealer = dealers.find(d => d.id === dealerId);
        return dealer ? dealer.dealerName : "";
    };

    const getProductName = (productId) => {
        const product = products.find(p => p.id === productId);
        return product ? product.prodname : "";
    };

    const handleSearch = (value) => {
        setSearch(value);

        const filteredData = purchases.filter(p =>
            p.purchaseno?.toLowerCase().includes(value.toLowerCase())
        );

        setFiltered(filteredData);
    };

    const handleEdit = (row) => {
        setEditData({
            id: row.id,
            purchaseno: row.purchaseno,
            dealerId: row.dealerId,
            purchasedate: row.purchasedate
        });
        setOpenEdit(true);
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete Purchase?",
            icon: "warning",
            showCancelButton: true
        });
        if (confirm.isConfirmed) {
            await deleteDoc(doc(db, "purchases", id));
            Swal.fire("Deleted", "Purchase removed", "success");
            loadPurchases();
        }
    };

    const updatePurchase = async () => {
        await updateDoc(doc(db, "purchases", editData.id), {
            purchaseno: editData.purchaseno,
            dealerId: editData.dealerId,
            purchasedate: editData.purchasedate
        });

        Swal.fire("Updated", "Purchase updated successfully", "success");
        setOpenEdit(false);
        loadPurchases();
    };

    const handleViewItems = (row) => {
        setSelectedItems(row.items || []);
        setPurchaseId(row.id);
        setOpenItems(true);
    };

    const handleItemChange = (index, field, value) => {
        const updated = [...selectedItems];
        updated[index][field] = value;
        const qty = Number(updated[index].qty || 0);
        const rate = Number(updated[index].rate || 0);
        const discount = Number(updated[index].discount || 0);
        updated[index].total = qty * rate;
        updated[index].finaltotal = (qty * rate) - discount;
        setSelectedItems(updated);
    };

    const addItem = () => {
        setSelectedItems([
            ...selectedItems,
            {
                productId: "",
                qty: "",
                rate: "",
                total: 0,
                discount: 0,
                finaltotal: 0
            }
        ]);
    };

    const removeItem = (index) => {
        const updated = [...selectedItems];
        updated.splice(index, 1);
        setSelectedItems(updated);
    };

    const saveItems = async () => {
        let subtotal = 0;
        let discount = 0;
        selectedItems.forEach(item => {
            subtotal += Number(item.total || 0);
            discount += Number(item.discount || 0);
        });

        const grandtotal = subtotal - discount;
        const purchase = purchases.find(p => p.id === purchaseId);
        const paid = Number(purchase?.paid || 0);
        const remaining = grandtotal - paid;

        await updateDoc(doc(db, "purchases", purchaseId), {
            items: selectedItems,
            subtotal: subtotal,
            discount: discount,
            grandtotal: grandtotal,
            remaining: remaining
        });

        Swal.fire("Updated", "Items & totals updated", "success");
        setOpenItems(false);
        loadPurchases();
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h5" fontWeight="bold">
                    Purchase List
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search Purchase No"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                />
            </Box>
            <Paper>
                <Table size="small">
                    <TableHead sx={{ background: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Purchase No</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Dealer</TableCell>
                            <TableCell>Grand Total</TableCell>
                            <TableCell>Paid</TableCell>
                            <TableCell>Remaining</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{row.purchaseno}</TableCell>
                                    <TableCell> {row.purchasedate} </TableCell>
                                    <TableCell>{getDealerName(row.dealerId)}</TableCell>
                                    <TableCell>{row.grandtotal}</TableCell>
                                    <TableCell>{row.paid}</TableCell>
                                    <TableCell>{row.remaining}</TableCell>
                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => handleViewItems(row)} >
                                            <Visibility />
                                        </IconButton>
                                        <IconButton color="secondary" onClick={() => handleEdit(row)}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(row.id)} >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={filtered.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                />
            </Paper>


            <Dialog open={openItems} maxWidth="md" fullWidth>
                <DialogTitle>
                    Purchase Products
                </DialogTitle>
                <DialogContent>
                    <Table size="small">
                        <TableHead sx={{ background: "#f5f5f5" }}>
                            <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell>Qty</TableCell>
                                <TableCell>Rate</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Final</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedItems.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <TextField
                                            select
                                            size="small"
                                            value={item.productId}
                                            onChange={(e) => handleItemChange(index, "productId", e.target.value)}
                                        >
                                            {products.map(p => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.productName}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.qty}
                                            onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.rate}
                                            onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                                        />

                                    </TableCell>

                                    <TableCell>{item.total}</TableCell>

                                    <TableCell>

                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.discount}
                                            onChange={(e) => handleItemChange(index, "discount", e.target.value)}
                                        />

                                    </TableCell>

                                    <TableCell>{item.finaltotal}</TableCell>

                                    <TableCell>

                                        <IconButton color="error" onClick={() => removeItem(index)}>
                                            <Delete />
                                        </IconButton>

                                    </TableCell>

                                </TableRow>

                            ))}

                        </TableBody>

                    </Table>

                    <Button
                        startIcon={<Add />}
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={addItem}
                    >
                        Add Product
                    </Button>

                </DialogContent>

                <DialogActions>

                    <Button onClick={() => setOpenItems(false)}>
                        Cancel
                    </Button>

                    <Button variant="contained" onClick={saveItems}>
                        Save
                    </Button>

                </DialogActions>

            </Dialog>

            <Dialog open={openEdit} maxWidth="sm" fullWidth>

                <DialogTitle>Edit Purchase</DialogTitle>

                <DialogContent>

                    <TextField
                        label="Purchase No"
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        value={editData.purchaseno}
                        onChange={(e) =>
                            setEditData({ ...editData, purchaseno: e.target.value })
                        }
                    />

                    <TextField
                        label="Purchase Date"
                        type="date"
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        InputLabelProps={{ shrink: true }}
                        value={editData.purchasedate}
                        onChange={(e) =>
                            setEditData({ ...editData, purchasedate: e.target.value })
                        }
                    />

                    <TextField
                        select
                        label="Dealer"
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        value={editData.dealerId}
                        onChange={(e) =>
                            setEditData({ ...editData, dealerId: e.target.value })
                        }
                    >

                        {dealers.map((d) => (
                            <MenuItem key={d.id} value={d.id}>
                                {d.dealerName}
                            </MenuItem>
                        ))}

                    </TextField>

                </DialogContent>

                <DialogActions>

                    <Button onClick={() => setOpenEdit(false)}>
                        Cancel
                    </Button>

                    <Button variant="contained" onClick={updatePurchase}>
                        Update
                    </Button>

                </DialogActions>

            </Dialog>

        </Box>

    );

}

export default Viewpurchasse;